import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { darkTheme } from '@stylesheets/theme';
import useAuth from '@contexts/useAuth';
import { billingGetOverview } from '@api/billing';
import { leaveOrganization } from '@api/organizations';
import PlanPicker from '@components/PlanPicker/PlanPicker';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import {
  Page,
  Background,
  ContentWrapper,
  LogoHeader,
  FooterLinks,
  LogoutLink,
  ReturnToPersonalLink,
  DialogError,
} from './PaymentSetup.styled';
import { Logo, BrandName } from '../Login/Login.styled';

const PaymentSetup: React.FC = () => {
  const { logout, user } = useAuth();
  const [isPersonalOrg, setIsPersonalOrg] = useState<boolean>(
    user?.isPersonalOrg ?? false
  );
  const [freeCredits, setFreeCredits] = useState<number>(
    user?.freeTierCredits ?? 0
  );
  const [leaving, setLeaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token) return;
    billingGetOverview(user.token)
      .then((overview) => {
        setIsPersonalOrg(overview.isPersonalOrg);
        setFreeCredits(overview.freeTierCredits);
      })
      .catch(() => {
        setIsPersonalOrg(user?.isPersonalOrg ?? false);
        setFreeCredits(user?.freeTierCredits ?? 0);
      });
  }, [user?.token, user?.isPersonalOrg, user?.freeTierCredits]);

  const orgName = user?.orgName?.trim() || 'your current organization';

  const handleConfirmLeave = async () => {
    if (!user?.token || leaving) return;
    setLeaving(true);
    setLeaveError(null);
    try {
      await leaveOrganization(user.token);
      globalThis.location.reload();
    } catch {
      setLeaving(false);
      setLeaveError(
        'Could not leave the organization. Please try again, or contact support if the problem persists.'
      );
    }
  };

  const openConfirm = () => {
    setLeaveError(null);
    setConfirmOpen(true);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Page>
        <Background />
        <ContentWrapper>
          <LogoHeader>
            <Logo />
            <BrandName>Aibuild OS</BrandName>
          </LogoHeader>

          <PlanPicker
            showFreeTier={isPersonalOrg}
            freeCredits={freeCredits}
          />

          <FooterLinks>
            {!isPersonalOrg && (
              <ReturnToPersonalLink
                onClick={openConfirm}
                disabled={leaving}
              >
                Leave organization and use a personal account
              </ReturnToPersonalLink>
            )}
            <LogoutLink onClick={logout}>Sign out</LogoutLink>
          </FooterLinks>
        </ContentWrapper>

        <ConfirmDialog
          show={confirmOpen}
          title="Leave organization?"
          confirmLabel={leaving ? 'Leaving…' : 'Leave organization'}
          cancelLabel="Stay"
          loading={leaving}
          onConfirm={handleConfirmLeave}
          onCancel={() => {
            if (leaving) return;
            setConfirmOpen(false);
            setLeaveError(null);
          }}
        >
          You&apos;ll leave <strong>{orgName}</strong> and switch to your own
          personal account. You&apos;ll lose access to the organization&apos;s
          assets and an admin will have to re-invite you to rejoin. This
          won&apos;t fix the organization&apos;s billing.
          {leaveError && <DialogError role="alert">{leaveError}</DialogError>}
        </ConfirmDialog>
      </Page>
    </ThemeProvider>
  );
};

export default PaymentSetup;
